import { useTranslation } from "react-i18next";

import BaseButton from "@/components_v2/Button/BaseButton";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import BaseSkeleton from "@/components_v2/Skeleton/BaseSkeleton";
import BaseSkeletonGroup from "@/components_v2/Skeleton/BaseSkeletonGroup";
import { SKELETON_VARIANTS } from "@/components_v2/Skeleton/constants";
import ContributionItem from "@/features/certifications/module/components/ContributionItem";
import RequirementBadge from "@/features/certifications/module/components/RequirementBadge";
import { CommunityCardProps } from "@/features/certifications/module/components/types";
import {
  getCommunityNudge,
  topicPath,
} from "@/features/certifications/module/utils";

import "./styles.css";

const ACTIVITY_SKELETON_ROWS = 2;

const CommunityCard = ({
  topic,
  posts,
  comments,
  user,
  activity,
  isActivityLoading,
  isActivityError,
}: CommunityCardProps) => {
  const { t } = useTranslation();

  let asks: string | null = null;
  let progress: string | null = null;
  if (posts && comments) {
    asks = t("certification.module.community.asksBoth", {
      posts: t("certification.module.community.postsCount", {
        count: posts.target,
      }),
      comments: t("certification.module.community.commentsCount", {
        count: comments.target,
      }),
    });
    progress = t("certification.module.community.progressBoth", {
      posts: t("certification.module.community.postsProgress", {
        current: posts.current,
        count: posts.target,
      }),
      comments: t("certification.module.community.commentsProgress", {
        current: comments.current,
        count: comments.target,
      }),
    });
  } else {
    const count = posts ?? comments;
    const kind = posts ? "posts" : "comments";
    if (count) {
      asks = t("certification.module.community.asksOne", {
        items: t(`certification.module.community.${kind}Count`, {
          count: count.target,
        }),
      });
      progress = t("certification.module.community.progressOne", {
        items: t(`certification.module.community.${kind}Progress`, {
          current: count.current,
          count: count.target,
        }),
      });
    }
  }

  return (
    <section className="certification-card module-card module-card-body flex flex-col gap-400">
      <div className="flex items-center justify-between gap-300">
        <h2 className="certification-heading header-s-medium">
          {t("certification.module.community.heading")}
        </h2>

        <RequirementBadge isRequired />
      </div>

      <div className="flex flex-col gap-300">
        <div className="flex flex-col gap-100">
          <span className="module-stat-label body-xs-regular">
            {t("certification.module.community.topicLabel")}
          </span>

          <h3 className="certification-heading header-s-medium">
            {topic.title}
          </h3>
        </div>

        <div className="flex flex-wrap items-center gap-300">
          <BaseButton
            type={BUTTON_TYPES.LINK}
            size={BUTTON_SIZES.SMALL}
            to={topicPath(topic.title)}
          >
            {t("certification.module.community.open")}
          </BaseButton>

          {asks && (
            <span className="certification-muted body-s-regular">{asks}</span>
          )}

          {progress && (
            <span className="certification-heading body-s-medium">
              {progress}
            </span>
          )}
        </div>
      </div>

      <div className="module-community-activity flex flex-col gap-300">
        <span className="module-stat-label body-xs-regular">
          {t("certification.module.community.activityLabel")}
        </span>

        {isActivityLoading && (
          <BaseSkeletonGroup
            label={t("labels.loading")}
            className="flex flex-col gap-400"
          >
            {Array.from({ length: ACTIVITY_SKELETON_ROWS }, (_, index) => (
              <div key={index} className="flex gap-300">
                <BaseSkeleton
                  variant={SKELETON_VARIANTS.CIRCLE}
                  width="var(--spacing-500)"
                  height="var(--spacing-500)"
                />

                <BaseSkeleton
                  variant={SKELETON_VARIANTS.TEXT}
                  lines={3}
                  className="flex-1"
                />
              </div>
            ))}
          </BaseSkeletonGroup>
        )}

        {!isActivityLoading && isActivityError && (
          <p className="certification-muted body-s-regular" role="alert">
            {t("certification.module.community.loadError")}
          </p>
        )}

        {!isActivityLoading && !isActivityError && activity.length === 0 && (
          <div className="flex flex-col items-start gap-200">
            <p className="certification-muted body-s-regular">
              {t("certification.module.community.empty")}
            </p>

            <BaseButton
              type={BUTTON_TYPES.LINK}
              size={BUTTON_SIZES.SMALL}
              to={topicPath(topic.title)}
            >
              {t("certification.module.community.postOnTopic")}
            </BaseButton>
          </div>
        )}

        {!isActivityLoading && activity.length > 0 && (
          <>
            <ul className="module-activity-list flex flex-col">
              {activity.map((item) => (
                <ContributionItem key={item.key} item={item} user={user} />
              ))}
            </ul>

            <div className="module-community-nudge flex flex-wrap items-center justify-between gap-300">
              <p className="certification-heading body-s-regular">
                {getCommunityNudge(t, posts, comments, activity)}
              </p>

              <BaseButton
                type={BUTTON_TYPES.LINK}
                variant={BUTTON_VARIANTS.TERTIARY}
                size={BUTTON_SIZES.EXTRA_SMALL}
                to={topicPath(topic.title)}
              >
                {t("certification.module.community.addComment")}
              </BaseButton>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default CommunityCard;
